import React, { useState, useEffect } from 'react'
import ClientList from './components/ClientList'
import ClientProfile from './components/ClientProfile'
import MeetingForm from './components/MeetingForm'
import NewClientForm from './components/NewClientForm'
import SearchBar from './components/SearchBar'
import FilterBar from './components/FilterBar'
import LoginForm from './components/LoginForm'
import AdminPanel from './components/AdminPanel'
import ClientSection from './components/ClientSection'
import DashboardSection from './components/DashboardSection'
import CoreUIDashboard from './components/CoreUIDashboard'
import AllTasksSection from './components/AllTasksSection'
import BudgetOperationsSection from './components/BudgetOperationsSection'
import CampaignTrackingSection from './components/CampaignTrackingSection'
import BalanceReductionSection from './components/BalanceReductionSection'
import NoContactClientsSection from './components/NoContactClientsSection'
import NoTrafficClientsSection from './components/NoTrafficClientsSection'
import ExcessJunkClientsSection from './components/ExcessJunkClientsSection'
import CpcTrafficClientsSection from './components/CpcTrafficClientsSection'
import ConversionDropsSection from './components/ConversionDropsSection'
import VerificationsSection from './components/VerificationsSection'
import NoAnswerClientsSection from './components/NoAnswerClientsSection'
import MetaSection from './components/MetaSection'
import MetaBudgetIncreaseSection from './components/MetaBudgetIncreaseSection'
import MetaCampaignTrackingSection from './components/MetaCampaignTrackingSection'
import MetaBalanceReductionSection from './components/MetaBalanceReductionSection'
import MetaNoContactSection from './components/MetaNoContactSection'
import MetaCostIncreaseSection from './components/MetaCostIncreaseSection'
import MetaVerificationsSection from './components/MetaVerificationsSection'
import MetaNoAnswerSection from './components/MetaNoAnswerSection'
import MetaBudgetReviewSection from './components/MetaBudgetReviewSection'
import GoogleAdsSection from './components/GoogleAdsSection'
import AdsSection from './components/ads/AdsSection'
import RemindsSection from './components/RemindsSection'
import AdsRequestSection from './components/AdsRequestSection'
import Layout from './components/Layout'
import { NotificationProvider } from './contexts/NotificationContext'
import {
  loadClients,
  createClient,
  updateClient,
  deleteClient,
  createMeeting,
  updateMeeting,
  loginUser
} from './api/index'
import { getUsers } from './mock/users'
import {
  requiresCellFiltering,
  getRolePermissions,
  getVisibleSections
} from './utils/rolePermissions'

const App = () => {
  const [clients, setClients] = useState([])
  const [filteredClients, setFilteredClients] = useState([])
  const [selectedClient, setSelectedClient] = useState(null)
  const [showMeetingForm, setShowMeetingForm] = useState(false)
  const [editingMeetingData, setEditingMeetingData] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    specialty: '',
    cell: '',
    month: '',
    sortBy: 'name',
    sortOrder: 'asc'
  })
  const [currentUser, setCurrentUser] = useState(() => {
    // Cargar usuario desde localStorage al inicializar
    const savedUser = localStorage.getItem('currentUser')
    return savedUser ? JSON.parse(savedUser) : null
  })
  const [showAdminPanel, setShowAdminPanel] = useState(false)
  const [currentPage, setCurrentPage] = useState(() => {
    // Cargar página actual desde localStorage al inicializar
    const savedPage = localStorage.getItem('currentPage')
    return savedPage || 'clients'
  })

  // Efecto para guardar la página actual en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('currentPage', currentPage)
  }, [currentPage])

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Pasar las células del usuario si requiere filtrado por células
        const userCells =
          currentUser && requiresCellFiltering(currentUser.role) && currentUser.cells
            ? currentUser.cells
            : null
        const clientsFromAPI = await loadClients(userCells)
        console.log('Clientes cargados desde la API:', clientsFromAPI)
        // Siempre cargar desde la API, sin datos de ejemplo
        setClients(clientsFromAPI || [])
      } catch (error) {
        console.error('Error loading clients:', error)
        // En caso de error, cargar array vacío
        setClients([])
      }
    }

    loadInitialData()
  }, [currentUser]) // Agregar currentUser como dependencia para recargar cuando cambie el usuario

  const handleLogin = async (email, password) => {
    try {
      const response = await loginUser(email, password)
      setCurrentUser(response.user)
      // Guardar usuario en localStorage para persistencia
      localStorage.setItem('currentUser', JSON.stringify(response.user))

      // El filtrado ahora se maneja automáticamente en ClientSection

      return response.user
    } catch (error) {
      throw error
    }
  }

  const handleLogout = () => {
    setCurrentUser(null)
    // Eliminar usuario y página actual de localStorage
    localStorage.removeItem('currentUser')
    localStorage.removeItem('currentPage')
    setSelectedClient(null)
    setShowMeetingForm(false)
    setEditingMeetingData(null)
    setShowAdminPanel(false)
    setCurrentPage('clients')
  }

  // El filtrado ahora se maneja en ClientSection

  const handleSaveMeeting = async (meetingToSave) => {
    try {
      console.log('Guardando junta:', meetingToSave)

      // Agregar clientId a los datos de la junta
      const meetingData = {
        ...meetingToSave,
        clientId: selectedClient.id
      }

      if (editingMeetingData) {
        // Actualizar junta existente
        await updateMeeting(meetingToSave.id, meetingData)
        console.log('Junta actualizada con ID:', meetingToSave.id)
      } else {
        // Crear nueva junta
        const response = await createMeeting(meetingData)
        console.log('Junta creada con ID:', response.id)
      }

      // Recargar la lista de clientes desde la API para obtener los datos actualizados
      const userCells =
        currentUser && requiresCellFiltering(currentUser.role) && currentUser.cells
          ? currentUser.cells
          : null
      const updatedClients = await loadClients(userCells)
      setClients(updatedClients)

      // Actualizar el cliente seleccionado
      const updatedSelectedClient = updatedClients.find((c) => c.id === selectedClient.id)
      setSelectedClient(updatedSelectedClient)

      setShowMeetingForm(false)
      setEditingMeetingData(null)

      alert(editingMeetingData ? 'Junta actualizada exitosamente.' : 'Junta creada exitosamente.')
    } catch (error) {
      console.error('Error al guardar junta:', error)
      alert('Error al guardar la junta. Por favor, inténtalo de nuevo.')
    }
  }

  const handleSaveNewClient = async (newClient) => {
    try {
      console.log('Guardando nuevo cliente:', newClient)

      // Llamar a la API para crear el cliente en la base de datos
      const response = await createClient(newClient)
      console.log('Cliente creado con ID:', response.id)

      // Recargar la lista de clientes desde la API para obtener el cliente recién creado
      const userCells =
        currentUser && requiresCellFiltering(currentUser.role) && currentUser.cells
          ? currentUser.cells
          : null
      const updatedClients = await loadClients(userCells)
      setClients(updatedClients)

      setCurrentPage('clients')
      alert('Cliente creado exitosamente.')
    } catch (error) {
      console.error('Error al crear cliente:', error)
      alert('Error al crear el cliente. Por favor, inténtalo de nuevo.')
    }
  }

  const handleBulkUploadClients = async (newClients) => {
    try {
      console.log('Cargando clientes masivamente:', newClients)

      // Crear cada cliente en la base de datos
      const createdClients = []
      for (const client of newClients) {
        try {
          const response = await createClient(client)
          console.log('Cliente creado con ID:', response.id)
          createdClients.push({ ...client, id: response.id })
        } catch (error) {
          console.error('Error al crear cliente:', client.name, error)
        }
      }

      // Recargar la lista de clientes desde la API para obtener los datos actualizados
      const userCells =
        currentUser && requiresCellFiltering(currentUser.role) && currentUser.cells
          ? currentUser.cells
          : null
      const updatedClients = await loadClients(userCells)
      setClients(updatedClients)

      setCurrentPage('clients')

      if (createdClients.length === newClients.length) {
        alert(`Se cargaron exitosamente ${createdClients.length} clientes.`)
      } else {
        alert(
          `Se cargaron ${createdClients.length} de ${newClients.length} clientes. Algunos clientes no pudieron ser creados.`
        )
      }
    } catch (error) {
      console.error('Error en carga masiva:', error)
      alert('Error durante la carga masiva. Por favor, inténtalo de nuevo.')
    }
  }

  const handleUpdateClient = async (updatedClient) => {
    try {
      // Extraer el ID y los datos de actualización
      const { id, ...updates } = updatedClient

      // Llamar a la API para actualizar el cliente en la base de datos
      await updateClient(id, updates)

      // Si la actualización en la API fue exitosa, actualizar el estado local
      const updatedClients = clients.map((client) =>
        client.id === updatedClient.id ? updatedClient : client
      )
      setClients(updatedClients)
      setSelectedClient(updatedClient)

      alert('Cliente actualizado exitosamente.')
    } catch (error) {
      console.error('Error al actualizar cliente:', error)
      throw error // Re-lanzar el error para que el componente pueda manejarlo
    }
  }

  const handleDeleteClient = async (clientId) => {
    if (
      window.confirm(
        '¿Estás seguro de que deseas eliminar este cliente? Esta acción no se puede deshacer.'
      )
    ) {
      try {
        // Llamar a la API para eliminar el cliente de la base de datos
        await deleteClient(clientId)

        // Si la eliminación en la API fue exitosa, actualizar el estado local
        const updatedClients = clients.filter((client) => client.id !== clientId)
        setClients(updatedClients)

        // Si el cliente eliminado era el seleccionado, limpiar la selección
        if (selectedClient && selectedClient.id === clientId) {
          setSelectedClient(null)
          setShowMeetingForm(false)
          setEditingMeetingData(null)
        }

        alert('Cliente eliminado exitosamente de la base de datos.')
      } catch (error) {
        console.error('Error al eliminar cliente:', error)
        alert('Error al eliminar el cliente. Por favor, inténtalo de nuevo.')
      }
    }
  }

  const handleEditLastMeeting = (meeting) => {
    setEditingMeetingData(meeting)
    setShowMeetingForm(true)
  }

  const handleSearch = (term) => {
    setSearchTerm(term)
  }

  const handleFilter = (newFilters) => {
    setFilters(newFilters)
  }

  if (!currentUser) {
    return <LoginForm onLogin={handleLogin} />
  }

  // Sistema de permisos basado en rolePermissions.js
  const rolePermissions = getRolePermissions(currentUser.role)

  const permissions = {
    // Permisos básicos desde rolePermissions
    canAccessAdminPanel: rolePermissions.canManageSystem || false,
    canManageUsers: rolePermissions.canCreateUsers || false,
    canViewDashboard: rolePermissions.canViewReports || currentUser.role === 'admin',
    canViewAllClients: rolePermissions.canViewClients || false,
    canEditAllClients: rolePermissions.canEditClients || false,
    canDeleteClients: rolePermissions.canDeleteClients || false,
    canEditAllMeetings: rolePermissions.canEditMeetings || false,
    canBulkUploadClients: currentUser.role === 'admin', // Solo admin
    canExportData: rolePermissions.canExportData || false,

    // Permisos de creación y edición
    canViewOwnClients: rolePermissions.canViewClients || false,
    canCreateClients: rolePermissions.canCreateClients || false,
    canCreateMeetings: rolePermissions.canCreateMeetings || false,
    canEditOwnMeetings: rolePermissions.canEditMeetings || false,
    canViewAllTasks: rolePermissions.canViewTasks || false,
    canCommentOnTasks: true, // Todos pueden comentar en tareas
    canCompleteOwnTasks: rolePermissions.canEditTasks || false,

    // Permisos específicos para todas las secciones de Google Ads
    canViewBudgetOperations: rolePermissions.canViewBudgetOperations,
    canCreateBudgetOperations: rolePermissions.canCreateBudgetOperations,
    canViewCampaignTracking: rolePermissions.canViewCampaignTracking,
    canCreateCampaignTracking: rolePermissions.canCreateCampaignTracking,
    canViewBalanceReduction: rolePermissions.canViewBalanceReduction,
    canCreateBalanceReduction: rolePermissions.canCreateBalanceReduction,
    canViewNoContactClients: rolePermissions.canViewNoContactClients,
    canCreateNoContactClients: rolePermissions.canCreateNoContactClients,
    canViewNoTrafficClients: rolePermissions.canViewNoTrafficClients,
    canCreateNoTrafficClients: rolePermissions.canCreateNoTrafficClients,
    canViewExcessJunkClients: rolePermissions.canViewExcessJunkClients,
    canCreateExcessJunkClients: rolePermissions.canCreateExcessJunkClients,
    canViewCpcTrafficClients: rolePermissions.canViewCpcTrafficClients,
    canCreateCpcTrafficClients: rolePermissions.canCreateCpcTrafficClients,
    canViewConversionDrops: rolePermissions.canViewConversionDrops,
    canCreateConversionDrops: rolePermissions.canCreateConversionDrops,
    canViewVerifications: rolePermissions.canViewVerifications,
    canCreateVerifications: rolePermissions.canCreateVerifications,
    canViewNoAnswerClients: rolePermissions.canViewNoAnswerClients,
    canCreateNoAnswerClients: rolePermissions.canCreateNoAnswerClients,

    // Permisos específicos para Google Ads
    canViewGoogleAds: rolePermissions.canViewGoogleAds,
    canCreateGoogleAds: rolePermissions.canCreateGoogleAds,
    canEditGoogleAds: rolePermissions.canEditGoogleAds,
    canDeleteGoogleAds: rolePermissions.canDeleteGoogleAds,
    canManageGoogleAdsAccounts: rolePermissions.canManageGoogleAdsAccounts,
    canConfigureGoogleAdsAlerts: rolePermissions.canConfigureGoogleAdsAlerts,
    canExportGoogleAdsReports: rolePermissions.canExportGoogleAdsReports,

    // Permisos específicos por células
    canAccessCell: (cellName) => {
      if (currentUser.role === 'admin') return true
      if (requiresCellFiltering(currentUser.role) && currentUser.cells) {
        return currentUser.cells.includes(cellName)
      }
      return false
    },

    // Permisos de edición de cliente específico
    canEditClient: (client) => {
      if (currentUser.role === 'admin') return true
      if (requiresCellFiltering(currentUser.role) && currentUser.cells && client.cell) {
        return currentUser.cells.includes(client.cell) && rolePermissions.canEditClients
      }
      return rolePermissions.canEditClients
    },

    // Permisos de visualización de cliente específico
    canViewClient: (client) => {
      if (currentUser.role === 'admin') return true
      if (requiresCellFiltering(currentUser.role) && currentUser.cells && client.cell) {
        return currentUser.cells.includes(client.cell)
      }
      return rolePermissions.canViewClients
    }
  }

  // Compatibilidad con código existente
  const canEditAll = permissions.canEditAllClients
  const canEditMeetings = permissions.canEditAllMeetings // Nuevo permiso específico para editar juntas
  const canCreateClientsAndMeetings = permissions.canCreateClients

  const renderContent = () => {
    if (currentPage === 'admin') {
      return <AdminPanel onBack={() => setCurrentPage('clients')} />
    } else if (currentPage === 'dashboard') {
      const handleSelectClientFromDashboard = (client) => {
        setSelectedClient(client)
        setCurrentPage('clients') // Cambiar a la página de clientes para mostrar el perfil
      }

      const handleNavigateToSection = (sectionId) => {
        setCurrentPage(sectionId)
      }

      return (
        <DashboardSection
          clients={clients}
          onSelectClient={handleSelectClientFromDashboard}
          onNavigateToSection={handleNavigateToSection}
          currentUser={currentUser}
          permissions={permissions}
        />
      )
    } else if (currentPage === 'allTasks') {
      return <AllTasksSection currentUser={currentUser} />
    } else if (currentPage === 'reminds') {
      return <RemindsSection currentUser={currentUser} />
    } else if (currentPage === 'budgetOperations') {
      return <BudgetOperationsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'campaignTracking') {
      return <CampaignTrackingSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'balanceReduction') {
      return <BalanceReductionSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'noContactClients') {
      return <NoContactClientsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'noTrafficClients') {
      return <NoTrafficClientsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'excessJunkClients') {
      return <ExcessJunkClientsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'cpcTrafficClients') {
      return <CpcTrafficClientsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'conversionDrops') {
      return <ConversionDropsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'verifications') {
      return <VerificationsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'noAnswerClients') {
      return <NoAnswerClientsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'meta') {
      return <MetaSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'metaBudgetIncrease') {
      return <MetaBudgetIncreaseSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'metaCampaignTracking') {
      return <MetaCampaignTrackingSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'metaBalanceReduction') {
      return <MetaBalanceReductionSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'metaNoContact') {
      return <MetaNoContactSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'metaCostIncrease') {
      return <MetaCostIncreaseSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'metaVerifications') {
      return <MetaVerificationsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'metaNoAnswer') {
      return <MetaNoAnswerSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'metaBudgetReview') {
      return <MetaBudgetReviewSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'googleAds') {
      return <GoogleAdsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'adsMonitoringSection') {
      return <AdsSection currentUser={currentUser} permissions={permissions} />
    } else if (currentPage === 'adsRequests') {
      return <AdsRequestSection currentUser={currentUser} permissions={permissions} />
    } else if (selectedClient) {
      if (showMeetingForm) {
        return (
          <MeetingForm
            client={selectedClient}
            onSave={handleSaveMeeting}
            onCancel={() => {
              setShowMeetingForm(false)
              setEditingMeetingData(null)
            }}
            initialMeetingData={editingMeetingData}
          />
        )
      } else {
        return (
          <ClientProfile
            client={selectedClient}
            onBack={() => setSelectedClient(null)}
            onNewMeeting={() => {
              setEditingMeetingData(null)
              setShowMeetingForm(true)
            }}
            onEditMeeting={canEditMeetings ? handleEditLastMeeting : null}
            onUpdateClient={canEditAll ? handleUpdateClient : null}
            canEdit={canEditAll}
            currentUser={currentUser}
          />
        )
      }
    } else {
      return (
        <ClientSection
          clients={clients}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          filters={filters}
          onFilter={handleFilter}
          onSelectClient={setSelectedClient}
          onNewMeeting={
            permissions.canCreateMeetings
              ? (client) => {
                  setSelectedClient(client)
                  setEditingMeetingData(null)
                  setShowMeetingForm(true)
                }
              : null
          }
          onSaveNewClient={permissions.canCreateClients ? handleSaveNewClient : null}
          onBulkUploadClients={permissions.canBulkUploadClients ? handleBulkUploadClients : null}
          canCreateClientsAndMeetings={permissions.canCreateClients}
          onUpdateClient={permissions.canEditAllClients ? handleUpdateClient : null}
          onDeleteClient={permissions.canDeleteClients ? handleDeleteClient : null}
          permissions={permissions}
          currentUser={currentUser}
        />
      )
    }
  }

  return (
    <NotificationProvider currentUser={currentUser}>
      <Layout
        currentUser={currentUser}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        permissions={permissions}
        onLogout={handleLogout}
      >
        {renderContent()}
      </Layout>
    </NotificationProvider>
  )
}

export default App
